import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../shadcn-ui/card';
import { Label } from '../shadcn-ui/label';
import { Input } from '../shadcn-ui/input';
import { CircleAlert, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFormContext } from 'react-hook-form';

const EstateDetailsCard = ({ loading }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <Card className="overflow-y-scroll rounded-2xl border border-border shadow-sm">
      <CardHeader className="px-4 pb-3">
        <CardTitle className="text-base font-medium">Estate details</CardTitle>
      </CardHeader>

      <CardContent className="space-y-3 p-4 pt-0">
        {/* Estate Name */}
        <div className="space-y-1.5">
          <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
            Estate Name<span className="">*</span>
          </Label>
          <div className="relative">
            <Input
              disabled={loading}
              placeholder="Free text - Estate Name"
              className={cn('rounded-[10px] bg-white text-sm font-normal', errors?.name && 'border-red-500')}
              {...register('name')}
            />
            {errors?.name && (
              <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
            )}
          </div>
          {errors?.name && <div className="text-xs text-red-500">{errors.name.message}</div>}
        </div>

        {/* Village */}
        <div className="space-y-1.5">
          <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
            Village<span className="">*</span>
          </Label>
          <div className="relative">
            <Input
              disabled={loading}
              placeholder="Village"
              className={cn(
                'rounded-[10px] bg-white text-sm font-normal',
                errors?.location?.village && 'border-red-500'
              )}
              {...register('location.village')}
            />
            {errors?.location?.village && (
              <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
            )}
          </div>

          {errors?.location?.village && <div className="text-xs text-red-500">{errors.location.village.message}</div>}
        </div>

        {/* Location */}
        <div className="space-y-1.5">
          <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">Location</Label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
            </span>

            <Input
              disabled={loading}
              placeholder="Address pin"
              className={cn(
                'rounded-[10px] bg-white pl-9 text-sm font-normal',
                errors?.location?.location_pin && 'border-red-500'
              )}
              {...register('location.location_pin')}
            />
            {errors?.location?.location_pin && (
              <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
            )}
          </div>

          {errors?.location?.location_pin && (
            <div className="text-xs text-red-500">{errors.location.location_pin.message}</div>
          )}
        </div>

        {/* Estate Area */}
        <div className="space-y-1.5">
          <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
            Estate Area (Acres)<span className="align-top">*</span>
          </Label>
          <div className="relative">
            <Input
              disabled={loading}
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              placeholder="Estate Area"
              className={cn('rounded-[10px] bg-white text-sm font-normal', errors?.size_in_acres && 'border-red-500')}
              {...register('size_in_acres')}
            />
            {errors?.size_in_acres && (
              <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
            )}
          </div>
          {errors?.size_in_acres && <div className="text-xs text-red-500">{errors.size_in_acres.message}</div>}
        </div>

        {/* Altitude */}
        <div className="space-y-1.5">
          <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">Altitude(m)</Label>
          <div className="relative">
            <Input
              disabled={loading}
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              placeholder="Altitude"
              className={cn('rounded-[10px] bg-white text-sm font-normal', errors?.altitude && 'border-red-500')}
              {...register('altitude')}
            />
            {errors?.altitude && (
              <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
            )}
          </div>
          {errors?.altitude && <div className="text-xs text-red-500">{errors.altitude.message}</div>}
        </div>

        {/* Estate Code */}
        <div className="space-y-1.5">
          <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
            Estate Code<span className="align-top">*</span>
          </Label>
          <div className="relative">
            <Input
              disabled={loading}
              placeholder="Estate Code"
              className={cn('rounded-[10px] bg-white text-sm font-normal', errors?.estate_code && 'border-red-500')}
              {...register('estate_code', {
                onChange: e => {
                  e.target.value = e.target.value.toUpperCase();
                },
              })}
            />
            {errors?.estate_code && (
              <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
            )}
          </div>
          {errors?.estate_code && <div className="text-xs text-red-500">{errors.estate_code.message}</div>}
        </div>
      </CardContent>
    </Card>
  );
};

export default EstateDetailsCard;
